import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { COMPANY_DETAILS, BUSINESS_NAME } from '@/src/constants';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  canonicalPath?: string;
  schema?: Record<string, any>;
}

export default function SEO({
  title,
  description = COMPANY_DETAILS.description,
  keywords = "cybersecurity consulting, vulnerability assessment, compliance support, cloud security, small business cybersecurity, retail cybersecurity, Washington DC cybersecurity, Virginia, Maryland",
  ogTitle,
  ogDescription,
  ogType = "website",
  ogUrl,
  ogImage = "https://agrandintl.com/og-image.jpg",
  canonicalPath,
  schema
}: SEOProps) {
  const location = useLocation();
  const baseUrl = "https://agrandintl.com";
  const currentUrl = `${baseUrl}${location.pathname}`;
  const canonicalUrl = canonicalPath ? `${baseUrl}${canonicalPath}` : currentUrl;
  
  const siteTitle = !title 
    ? `${BUSINESS_NAME} – Cybersecurity Consulting for Retail & Cloud` 
    : (title.includes(BUSINESS_NAME) ? title : `${title} | ${BUSINESS_NAME}`);

  return (
    <Helmet>
      {/* Standard metadata tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle || siteTitle} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:url" content={ogUrl || currentUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={BUSINESS_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || siteTitle} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Favicons (Redundant check if in index.html, but good for dynamic) */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />

      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}
